// =============================================================================
// Curated emoji set for the reaction picker.
//
// We don't ship the full Unicode emoji catalog — that's ~3,500 entries and
// most aren't useful for chat reactions. This is a hand-picked ~140 covering
// the categories friend groups actually use. Each entry has a glyph and
// space-separated keywords for the search box.
// =============================================================================

export interface EmojiEntry {
  /** The glyph itself (may be multi-codepoint). */
  glyph: string;
  /** Search keywords (space-separated). */
  keywords: string;
}

export interface EmojiCategory {
  name: string;
  emoji: EmojiEntry[];
}

// The "quick" row that appears as a one-tap shortcut above the picker.
// These are the ones friend groups use most often.
export const QUICK_REACTIONS: string[] = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🔥', '👀'];

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: 'Smileys',
    emoji: [
      { glyph: '😀', keywords: 'grinning happy smile' },
      { glyph: '😁', keywords: 'beaming grin smile' },
      { glyph: '😂', keywords: 'joy laugh tears lol' },
      { glyph: '🤣', keywords: 'rofl rolling laugh lol' },
      { glyph: '😅', keywords: 'sweat smile nervous' },
      { glyph: '😊', keywords: 'blush smile happy' },
      { glyph: '😍', keywords: 'love heart eyes' },
      { glyph: '🥰', keywords: 'love hearts smile' },
      { glyph: '😘', keywords: 'kiss heart' },
      { glyph: '😎', keywords: 'cool sunglasses' },
      { glyph: '🤩', keywords: 'star eyes amazed' },
      { glyph: '🤔', keywords: 'thinking hmm' },
      { glyph: '😐', keywords: 'neutral meh' },
      { glyph: '😬', keywords: 'grimace yikes awkward' },
      { glyph: '🙄', keywords: 'eye roll annoyed' },
      { glyph: '😴', keywords: 'sleeping zzz tired' },
      { glyph: '😪', keywords: 'sleepy tired' },
      { glyph: '🥱', keywords: 'yawn bored' },
      { glyph: '😢', keywords: 'cry sad tear' },
      { glyph: '😭', keywords: 'cry sob loud' },
      { glyph: '😤', keywords: 'huff steam triumph' },
      { glyph: '😠', keywords: 'angry mad' },
      { glyph: '😡', keywords: 'rage angry mad' },
      { glyph: '🤬', keywords: 'cursing swear angry' },
      { glyph: '😱', keywords: 'scream shock' },
      { glyph: '😨', keywords: 'fearful scared' },
      { glyph: '😰', keywords: 'sweat anxious worried' },
      { glyph: '🥲', keywords: 'tear smile bittersweet' },
      { glyph: '🥺', keywords: 'pleading puppy eyes' },
      { glyph: '😮', keywords: 'wow surprised oh' },
      { glyph: '😳', keywords: 'flushed embarrassed' },
      { glyph: '🤯', keywords: 'mind blown wow' },
      { glyph: '🤠', keywords: 'cowboy yeehaw' },
      { glyph: '🥳', keywords: 'party celebrate birthday' },
      { glyph: '😈', keywords: 'devil mischief' },
      { glyph: '🤡', keywords: 'clown joke' },
      { glyph: '💀', keywords: 'skull dead lol' },
      { glyph: '☠️', keywords: 'skull crossbones dead' },
      { glyph: '👻', keywords: 'ghost spooky' },
    ],
  },
  {
    name: 'Gestures',
    emoji: [
      { glyph: '👍', keywords: 'thumbs up yes ok approve' },
      { glyph: '👎', keywords: 'thumbs down no boo' },
      { glyph: '👏', keywords: 'clap applause bravo' },
      { glyph: '🙌', keywords: 'praise raise hands hooray' },
      { glyph: '🙏', keywords: 'pray thanks please' },
      { glyph: '🤝', keywords: 'handshake deal' },
      { glyph: '✋', keywords: 'high five hand stop' },
      { glyph: '👋', keywords: 'wave hi bye' },
      { glyph: '🤙', keywords: 'shaka call hang loose' },
      { glyph: '🤞', keywords: 'fingers crossed luck' },
      { glyph: '🤘', keywords: 'rock metal horns' },
      { glyph: '👌', keywords: 'ok perfect' },
      { glyph: '🤌', keywords: 'pinched chefs kiss italian' },
      { glyph: '🫶', keywords: 'heart hands love' },
      { glyph: '✌️', keywords: 'peace victory' },
      { glyph: '💪', keywords: 'flex strong muscle' },
      { glyph: '🫡', keywords: 'salute respect' },
      { glyph: '🫠', keywords: 'melt dying lol' },
    ],
  },
  {
    name: 'Hearts',
    emoji: [
      { glyph: '❤️', keywords: 'red heart love' },
      { glyph: '🧡', keywords: 'orange heart' },
      { glyph: '💛', keywords: 'yellow heart' },
      { glyph: '💚', keywords: 'green heart' },
      { glyph: '💙', keywords: 'blue heart' },
      { glyph: '💜', keywords: 'purple heart' },
      { glyph: '🖤', keywords: 'black heart' },
      { glyph: '🤍', keywords: 'white heart' },
      { glyph: '🩷', keywords: 'pink heart' },
      { glyph: '🩵', keywords: 'light blue heart' },
      { glyph: '💕', keywords: 'two hearts' },
      { glyph: '💖', keywords: 'sparkling heart' },
      { glyph: '💝', keywords: 'heart gift ribbon' },
      { glyph: '💔', keywords: 'broken heart' },
    ],
  },
  {
    name: 'Animals',
    emoji: [
      { glyph: '🐶', keywords: 'dog puppy' },
      { glyph: '🐱', keywords: 'cat kitten' },
      { glyph: '🐭', keywords: 'mouse' },
      { glyph: '🐹', keywords: 'hamster' },
      { glyph: '🐰', keywords: 'rabbit bunny' },
      { glyph: '🦊', keywords: 'fox' },
      { glyph: '🐻', keywords: 'bear' },
      { glyph: '🐼', keywords: 'panda' },
      { glyph: '🐨', keywords: 'koala' },
      { glyph: '🐯', keywords: 'tiger' },
      { glyph: '🦁', keywords: 'lion' },
      { glyph: '🐮', keywords: 'cow' },
      { glyph: '🐷', keywords: 'pig' },
      { glyph: '🐸', keywords: 'frog' },
      { glyph: '🦄', keywords: 'unicorn' },
      { glyph: '🐝', keywords: 'bee' },
      { glyph: '🦋', keywords: 'butterfly' },
      { glyph: '🐢', keywords: 'turtle' },
      { glyph: '🐙', keywords: 'octopus' },
      { glyph: '🦑', keywords: 'squid' },
      { glyph: '🐳', keywords: 'whale' },
      { glyph: '🦈', keywords: 'shark' },
    ],
  },
  {
    name: 'Food',
    emoji: [
      { glyph: '🍎', keywords: 'apple fruit' },
      { glyph: '🍌', keywords: 'banana' },
      { glyph: '🍕', keywords: 'pizza' },
      { glyph: '🍔', keywords: 'burger' },
      { glyph: '🍟', keywords: 'fries' },
      { glyph: '🌭', keywords: 'hot dog' },
      { glyph: '🌮', keywords: 'taco' },
      { glyph: '🌯', keywords: 'burrito' },
      { glyph: '🥪', keywords: 'sandwich' },
      { glyph: '🍣', keywords: 'sushi' },
      { glyph: '🍜', keywords: 'ramen noodles' },
      { glyph: '🍩', keywords: 'donut' },
      { glyph: '🍪', keywords: 'cookie' },
      { glyph: '🎂', keywords: 'birthday cake' },
      { glyph: '🍰', keywords: 'cake slice' },
      { glyph: '🍦', keywords: 'ice cream' },
      { glyph: '☕', keywords: 'coffee' },
      { glyph: '🍺', keywords: 'beer' },
      { glyph: '🍷', keywords: 'wine' },
      { glyph: '🍸', keywords: 'cocktail martini' },
      { glyph: '🥂', keywords: 'cheers champagne' },
    ],
  },
  {
    name: 'Activity',
    emoji: [
      { glyph: '⚽', keywords: 'soccer football' },
      { glyph: '🏀', keywords: 'basketball' },
      { glyph: '🏈', keywords: 'football' },
      { glyph: '⚾', keywords: 'baseball' },
      { glyph: '🎾', keywords: 'tennis' },
      { glyph: '🏐', keywords: 'volleyball' },
      { glyph: '🎱', keywords: 'pool 8 ball' },
      { glyph: '🎮', keywords: 'video games gaming' },
      { glyph: '🎲', keywords: 'dice' },
      { glyph: '🎯', keywords: 'darts target bullseye' },
      { glyph: '🎤', keywords: 'mic karaoke' },
      { glyph: '🎧', keywords: 'headphones music' },
      { glyph: '🎸', keywords: 'guitar' },
      { glyph: '🥁', keywords: 'drum' },
      { glyph: '🎉', keywords: 'party celebrate' },
      { glyph: '🎊', keywords: 'confetti party' },
      { glyph: '🎁', keywords: 'gift present' },
    ],
  },
  {
    name: 'Objects',
    emoji: [
      { glyph: '🔥', keywords: 'fire lit' },
      { glyph: '✨', keywords: 'sparkles shine' },
      { glyph: '💯', keywords: 'hundred 100 perfect' },
      { glyph: '⭐', keywords: 'star' },
      { glyph: '🌟', keywords: 'glowing star' },
      { glyph: '💥', keywords: 'boom explosion' },
      { glyph: '⚡', keywords: 'lightning bolt' },
      { glyph: '🌈', keywords: 'rainbow' },
      { glyph: '☀️', keywords: 'sun' },
      { glyph: '🌙', keywords: 'moon night' },
      { glyph: '👀', keywords: 'eyes looking' },
      { glyph: '💩', keywords: 'poop' },
      { glyph: '💎', keywords: 'gem diamond' },
      { glyph: '💰', keywords: 'money bag' },
      { glyph: '💸', keywords: 'money flying' },
      { glyph: '🚀', keywords: 'rocket' },
      { glyph: '✅', keywords: 'check yes done' },
      { glyph: '❌', keywords: 'x no wrong' },
      { glyph: '⚠️', keywords: 'warning' },
      { glyph: '❓', keywords: 'question' },
      { glyph: '❗', keywords: 'exclamation' },
    ],
  },
];
